var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Web equivalents of the shared mobile actions that the wishlist block uses
 * besides the wishlist-specific surface: authentication/open, product/open,
 * and cart/add. These implementations assume a top-level browsing context
 * on the merchant's Shopify storefront — i.e. the bridge is rendered
 * directly, not inside a cross-origin iframe.
 *
 * Iframe mode (Theme App Extension serving Tapcart-hosted content) requires
 * a different implementation: actions postMessage to the parent storefront,
 * which runs a small host script that performs the real navigation /
 * cart-mutation. Those overrides are wired via `registerActionImpl` at
 * Theme App Extension boot — see task #7.
 */
/**
 * Extract a numeric Shopify ID from a string that might be either:
 *   - a raw numeric ID ("12345")
 *   - a Shopify GID ("gid://shopify/Product/12345")
 *
 * Returns the original string if no numeric tail is found.
 *
 * Note: base64-encoded GIDs are NOT supported here — they don't end in a
 * literal numeric tail, so the trailing-digit regex would never match
 * the numeric part. The Tapcart blocks that drive this code path all
 * pass either raw numeric IDs (App Studio's normalized product IDs) or
 * unencoded GIDs (Shopify Storefront API responses), so adding base64
 * decode here would be dead code. If a caller ever needs to feed in a
 * base64 GID, decode it upstream first.
 */
const numericId = (input) => {
    const match = input.match(/(\d+)\s*$/);
    return match ? match[1] : input;
};
export const authenticationOpen = (_data, handlers) => {
    var _a, _b;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    // Shopify renders the appropriate sign-in page at /account/login for both
    // Classic and New Customer Accounts — the storefront handles the routing
    // and post-login redirect.
    window.location.href = "/account/login";
    (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, {});
};
export const productOpen = (data, handlers) => {
    var _a, _b, _c, _d, _e;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    const payload = (data !== null && data !== void 0 ? data : {});
    const slug = (_c = (_b = payload.productHandle) !== null && _b !== void 0 ? _b : payload.handle) !== null && _c !== void 0 ? _c : (payload.productId ? numericId(payload.productId) : undefined);
    if (!slug) {
        (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, new Error("product/open: missing productHandle/handle/productId"));
        return;
    }
    const variantParam = payload.variantId
        ? `?variant=${encodeURIComponent(numericId(payload.variantId))}`
        : "";
    // Shopify's storefront resolves /products/{numericId} via redirect to the
    // canonical /products/{handle} URL; using the handle is preferred but
    // numeric ID is a safe fallback when the block doesn't have the handle.
    window.location.href = `/products/${encodeURIComponent(slug)}${variantParam}`;
    (_e = handlers.onSuccess) === null || _e === void 0 ? void 0 : _e.call(handlers, {});
};
/**
 * Navigate to a collection page. Same slug-resolution rules as
 * product/open: prefer the handle, fall back to the numeric tail of the
 * id (Shopify redirects /collections/{numericId} to the canonical
 * handle URL).
 */
export const collectionOpen = (data, handlers) => {
    var _a, _b, _c, _d, _e;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    const payload = (data !== null && data !== void 0 ? data : {});
    const slug = (_c = (_b = payload.collectionHandle) !== null && _b !== void 0 ? _b : payload.handle) !== null && _c !== void 0 ? _c : (payload.collectionId ? numericId(payload.collectionId) : undefined);
    if (!slug) {
        (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, new Error("collection/open: missing collectionHandle/handle/collectionId"));
        return;
    }
    window.location.href = `/collections/${encodeURIComponent(slug)}`;
    (_e = handlers.onSuccess) === null || _e === void 0 ? void 0 : _e.call(handlers, {});
};
/**
 * Browser-history back. Inside the iframe this walks the iframe's own
 * history (screen-to-screen navigation within the embed), which matches
 * the mobile semantic of popping the screen stack.
 */
export const goBack = (_data, handlers) => {
    var _a, _b;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    window.history.back();
    (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, {});
};
export const cartAdd = (data, handlers, ctx) => {
    var _a, _b, _c;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    const payload = (data !== null && data !== void 0 ? data : {});
    if (!payload.variantId) {
        (_b = handlers.onError) === null || _b === void 0 ? void 0 : _b.call(handlers, new Error("cart/add: variantId required"));
        return;
    }
    const id = Number(numericId(payload.variantId));
    if (!Number.isFinite(id)) {
        (_c = handlers.onError) === null || _c === void 0 ? void 0 : _c.call(handlers, new Error(`cart/add: variantId "${payload.variantId}" not numeric`));
        return;
    }
    ;
    (() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        try {
            const addRes = yield fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    id,
                    quantity: (_a = payload.quantity) !== null && _a !== void 0 ? _a : 1,
                    properties: payload.properties,
                }),
                // Include first-party cart cookie.
                credentials: "same-origin",
            });
            if (!addRes.ok) {
                const errBody = yield addRes.text().catch(() => "");
                throw new Error(`cart/add ${addRes.status}: ${errBody}`);
            }
            const lineItem = yield addRes.json();
            // Refresh the cart variable so blocks reading `variables.cart` see the
            // new state. Failure here is non-fatal — the add itself succeeded.
            try {
                const cartRes = yield fetch("/cart.js", {
                    headers: { Accept: "application/json" },
                    credentials: "same-origin",
                });
                if (cartRes.ok) {
                    const cart = (yield cartRes.json());
                    ctx.variables.setCart(cart);
                }
            }
            catch (err) {
                console.warn("[storefront-bridge] cart/add: cart refresh failed", err);
            }
            (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, lineItem);
        }
        catch (err) {
            // Normalize the catch-typed `unknown` so onError consumers always
            // receive an Error (fetch can reject with non-Error values in some
            // embedder environments).
            const error = err instanceof Error ? err : new Error(String(err));
            (_d = (_c = ctx.config).onError) === null || _d === void 0 ? void 0 : _d.call(_c, error, "cart/add");
            (_e = handlers.onError) === null || _e === void 0 ? void 0 : _e.call(handlers, error);
        }
    }))();
};
